import { useState, useCallback, useEffect } from 'react';
import { weatherService } from '../services/weather';

const CACHE_KEY = 'weather_pollen_cache';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const CACHE_VERSION = 1; // Increment this to invalidate old cache
const GEO_REQUESTED_KEY = 'weather_geo_requested'; // Track if we've asked for geolocation

export function useWeather() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check if cached data is still valid
    const getCachedData = useCallback(() => {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const { timestamp, payload, version } = JSON.parse(cached);

            // Invalidate cache if version changed
            if (version !== CACHE_VERSION) {
                sessionStorage.removeItem(CACHE_KEY);
                return null;
            }

            const age = Date.now() - timestamp;

            if (age < CACHE_TTL_MS) {
                return payload;
            }
            // Clear expired cache
            sessionStorage.removeItem(CACHE_KEY);
        } catch (e) {
            console.warn('Cache read error:', e);
        }
        return null;
    }, []);

    // Save data to cache
    const setCacheData = useCallback((payload) => {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                version: CACHE_VERSION,
                payload
            }));
        } catch (e) {
            console.warn('Cache write error:', e);
        }
        setData(payload);
    }, []);

    // Fetch with location (geolocation)
    const fetchWithLocation = useCallback(async () => {
        const cached = getCachedData();
        if (cached) {
            setData(cached);
            setLoading(false);
            return cached;
        }

        setLoading(true);
        setError(null);

        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const { latitude, longitude } = position.coords;
                        const result = await weatherService.getPollenWithCoords(latitude, longitude);
                        setCacheData(result);
                        setError(null);
                        setLoading(false);
                        resolve(result);
                    } catch (err) {
                        console.error('Fetch with coords failed, trying fallback:', err);
                        // Fallback to default
                        try {
                            const result = await weatherService.getPollenDefault();
                            setCacheData(result);
                            setError(null);
                            setLoading(false);
                            resolve(result);
                        } catch (fallbackErr) {
                            console.error('Fallback fetch failed:', fallbackErr);
                            setError('Kunne ikke indlæse pollendata');
                            setLoading(false);
                            resolve(null);
                        }
                    }
                },
                (geoErr) => {
                    console.warn('Geolocation denied or error, trying fallback:', geoErr);
                    // Fallback to default when location denied/unavailable
                    const cached = getCachedData();
                    if (cached) {
                        setData(cached);
                        setLoading(false);
                        resolve(cached);
                        return;
                    }

                    setLoading(true);
                    weatherService.getPollenDefault()
                        .then((result) => {
                            setCacheData(result);
                            setError(null);
                            setLoading(false);
                            resolve(result);
                        })
                        .catch((err) => {
                            console.error('Fallback fetch failed:', err);
                            setError('Kunne ikke indlæse pollendata');
                            setLoading(false);
                            resolve(null);
                        });
                },
                { maximumAge: 60000, timeout: 5000 }
            );
        });
    }, [getCachedData, setCacheData]);

    // Fetch with default/fallback (no params)
    const fetchDefault = useCallback(async () => {
        const cached = getCachedData();
        if (cached) {
            setData(cached);
            setLoading(false);
            return cached;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await weatherService.getPollenDefault();
            setCacheData(result);
            setError(null);
            return result;
        } catch (err) {
            console.error('Fetch default failed:', err);
            setError('Kunne ikke indlæse pollendata');
            return null;
        } finally {
            setLoading(false);
        }
    }, [getCachedData, setCacheData]);

    // Fetch by region
    const fetchRegion = useCallback(async (regionKey) => {
        // Don't cache region-specific requests in the same slot, or use a separate cache per region
        // For simplicity, we'll cache it but clear when switching regions
        const cached = getCachedData();
        const decodedKey = regionKey.toLowerCase();

        // Check if we have the same region cached
        if (cached && cached._regionKey === decodedKey) {
            setData(cached);
            setLoading(false);
            return cached;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await weatherService.getPollenByRegion(decodedKey);
            const withRegionKey = { ...result, _regionKey: decodedKey };
            setCacheData(withRegionKey);
            setError(null);
            return result;
        } catch (err) {
            console.error('Fetch region failed:', err);
            setError('Kunne ikke indlæse pollendata for regionen');
            return null;
        } finally {
            setLoading(false);
        }
    }, [getCachedData, setCacheData]);

    // Auto-fetch location on mount (browser geolocation popup)
    useEffect(() => {
        const hasAskedForGeo = localStorage.getItem(GEO_REQUESTED_KEY);
        if (!hasAskedForGeo && navigator.geolocation) {
            // Mark that we've requested geolocation (only ask once)
            localStorage.setItem(GEO_REQUESTED_KEY, 'true');
            // Trigger the geolocation request (browser will show popup)
            fetchWithLocation();
        }
    }, [fetchWithLocation]);

    return {
        data,
        loading,
        error,
        fetchWithLocation,
        fetchDefault,
        fetchRegion
    };
}
